// src/api/tecnicos.js - API para manejo de técnicos

import { apiFetch, API_CONFIG } from './index.js';

// ==================== CONSTANTES ====================

// Estados posibles para técnicos
export const ESTADOS_TECNICO = ['activo', 'inactivo'];

// ==================== FUNCIONES AUXILIARES (NO API) ====================

/**
 * Convertir de snake_case (API) a camelCase (frontend)
 * @param {Object} apiTecnico - Técnico desde la API
 * @returns {Object} Técnico convertido
 */
const convertirDesdeAPI = (apiTecnico) => {
  if (!apiTecnico) return null;
  
  // Si viene en un wrapper { data: { ... } }
  let source = apiTecnico;
  if (apiTecnico.data && !Array.isArray(apiTecnico.data) && typeof apiTecnico.data === 'object') {
    source = apiTecnico.data;
  }
  
  return {
    idTecnico: source.idTecnico || source.id || source.id_tecnico,
    idUsuario: source.idUsuario || source.id_usuario,
    nombre: source.nombre,
    apellido: source.apellido,
    descripcion: source.descripcion,
    especialidad: source.especialidad,
    comisionPorcentaje: source.comisionPorcentaje,
    fechaContratacion: source.fechaContratacion,
    estado: source.estado || 'activo',
    // Mantener también los campos originales
    ...apiTecnico,
    ...source,
    // Asegurar normalización
    idTecnico: source.idTecnico || source.id || apiTecnico.idTecnico || apiTecnico.id
  };
};

/**
 * Convertir de camelCase (frontend) a snake_case (API)
 * @param {Object} frontendTecnico - Técnico desde el frontend
 * @returns {Object} Técnico para la API
 */
const convertirParaAPI = (frontendTecnico) => {
  return {
    idUsuario: frontendTecnico.idUsuario,
    nombre: frontendTecnico.nombre,
    apellido: frontendTecnico.apellido || '',
    descripcion: frontendTecnico.descripcion || '',
    especialidad: frontendTecnico.especialidad || '',
    comisionPorcentaje: frontendTecnico.comisionPorcentaje || 0,
    fechaContratacion: frontendTecnico.fechaContratacion || new Date().toISOString().split('T')[0],
    estado: frontendTecnico.estado || 'activo'
  };
};

/**
 * Convertir array de técnicos desde API
 * @param {Array} apiTecnicos - Array de técnicos desde API
 * @returns {Array} Array de técnicos convertidos
 */
const convertirListaDesdeAPI = (apiTecnicos) => {
  let lista = apiTecnicos;
  if (apiTecnicos && apiTecnicos.data && Array.isArray(apiTecnicos.data)) {
    lista = apiTecnicos.data;
  }
  if (!Array.isArray(lista)) return [];
  return lista.map(convertirDesdeAPI);
};

/**
 * Formatear fecha de contratación
 * @param {string} fechaISO - Fecha en formato ISO
 * @param {boolean} completo - Si incluir hora completa
 * @returns {string} Fecha formateada
 */
export const formatearFechaContratacion = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    const opciones = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    
    if (completo) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
    }
    
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch (error) {
    return fechaISO;
  }
};

/**
 * Convertir estado a texto legible
 * @param {string} estado - Estado del técnico
 * @returns {string} Estado formateado
 */
export const convertirEstadoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  return estado.charAt(0).toUpperCase() + estado.slice(1);
};

// ==================== FUNCIONES API ====================

/**
 * Obtener todos los técnicos
 * @returns {Promise} Lista de técnicos
 */
export const obtenerTecnicos = async () => {
  try {
    console.log('📋 Obteniendo técnicos...');
    const response = await apiFetch('/Tecnico');
    const tecnicos = convertirListaDesdeAPI(response);
    
    return {
      success: true,
      data: tecnicos,
      count: tecnicos.length,
      message: 'Técnicos obtenidos exitosamente'
    };
  } catch (error) {
    console.error('❌ Error obteniendo técnicos:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener un técnico por ID de usuario
 * @param {number} idUsuario - ID del usuario
 * @returns {Promise} Datos del técnico
 */
export const obtenerTecnicoPorIdUsuario = async (idUsuario) => {
  try {
    if (!idUsuario) throw new Error('ID de usuario es requerido');
    
    // Intentamos obtener todos y filtrar localmente para mayor compatibilidad
    const response = await obtenerTecnicos();
    if (response.success) {
      const tecnico = response.data.find(t => 
        parseInt(t.idUsuario) === parseInt(idUsuario) || 
        parseInt(t.id_usuario) === parseInt(idUsuario)
      );
      
      return {
        success: !!tecnico,
        data: tecnico || null,
        message: tecnico ? 'Técnico encontrado' : 'No se encontró un técnico asociado a este usuario'
      };
    }
    return response;
  } catch (error) {
    console.error(`❌ Error buscando técnico por usuario ${idUsuario}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Obtener un técnico por ID
 * @param {number} id - ID del técnico
 * @returns {Promise} Datos del técnico
 */
export const obtenerTecnicoPorId = async (id) => {
  try {
    if (!id) throw new Error('ID de técnico es requerido');
    const response = await apiFetch(`/Tecnico/${id}`);
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Técnico obtenido exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error obteniendo técnico ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Crear un nuevo técnico
 * @param {Object} tecnicoData - Datos del técnico
 * @returns {Promise} Técnico creado
 */
export const crearTecnico = async (tecnicoData) => {
  try {
    if (!tecnicoData.idUsuario) throw new Error('El ID de usuario es requerido');
    if (!tecnicoData.nombre) throw new Error('El nombre es requerido');
    
    console.log('➕ Creando nuevo técnico:', tecnicoData);
    const apiData = convertirParaAPI(tecnicoData);
    
    const response = await apiFetch('/Tecnico', {
      method: 'POST',
      body: JSON.stringify(apiData)
    });
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Técnico creado exitosamente'
    };
  } catch (error) {
    console.error('❌ Error creando técnico:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Actualizar un técnico existente
 * @param {number} id - ID del técnico
 * @param {Object} tecnicoData - Datos a actualizar
 * @returns {Promise} Técnico actualizado
 */
export const actualizarTecnico = async (id, tecnicoData) => {
  try {
    if (!id) throw new Error('ID de técnico es requerido');
    
    console.log(`✏️ Actualizando técnico ID: ${id}`, tecnicoData);
    const apiData = {
      ...convertirParaAPI(tecnicoData),
      idTecnico: parseInt(id)
    };
    
    const response = await apiFetch(`/Tecnico/${id}`, {
      method: 'PUT',
      body: JSON.stringify(apiData)
    });
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Técnico actualizado exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error actualizando técnico ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Eliminar un técnico
 * @param {number} id - ID del técnico a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarTecnico = async (id) => {
  try {
    if (!id) throw new Error('ID de técnico es requerido');
    await apiFetch(`/Tecnico/${id}`, { method: 'DELETE' });
    
    return { success: true, message: 'Técnico eliminado exitosamente' };
  } catch (error) {
    console.error(`❌ Error eliminando técnico ${id}:`, error);
    return { success: false, error: error.message };
  }
};
